import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CreditCard as CreditCardIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { stripeConnectApi, ConnectStatus } from '../../services/stripeConnectApi';
import StripeConnectChoiceDialog, { ConnectionType } from '../../components/stripe/StripeConnectChoiceDialog';

export const PaymentSettingsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);

  const success = searchParams.get('success');
  const error = searchParams.get('error');
  const refresh = searchParams.get('refresh');

  // Clear URL params after showing message
  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => setSearchParams({}), 5000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [success, error, setSearchParams]);

  // Handle refresh param (for Account Links flow)
  useEffect(() => {
    if (refresh === 'true') {
      setSearchParams({});
    }
  }, [refresh, setSearchParams]);

  // Fetch connection status
  const { data: status, isLoading } = useQuery<ConnectStatus>({
    queryKey: ['stripe-connect-status'],
    queryFn: stripeConnectApi.getStatus,
  });

  // Handle connection choice from modal
  const handleConnectionChoice = async (connectionType: ConnectionType) => {
    setIsConnecting(true);
    try {
      let url: string;
      if (connectionType === 'existing') {
        url = await stripeConnectApi.getOAuthUrl();
      } else {
        url = await stripeConnectApi.getOnboardingUrl();
      }
      window.location.href = url;
    } catch (err) {
      console.error('Failed to get Stripe URL:', err);
      setIsConnecting(false);
      setChoiceDialogOpen(false);
    }
  };

  // Handle "Complete Setup" for incomplete accounts
  const handleCompleteSetup = async () => {
    setIsConnecting(true);
    try {
      const url = await stripeConnectApi.getRefreshUrl();
      window.location.href = url;
    } catch (err) {
      console.error('Failed to get refresh URL:', err);
      setIsConnecting(false);
    }
  };

  // Disconnect mutation
  const disconnectMutation = useMutation({
    mutationFn: stripeConnectApi.disconnect,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stripe-connect-status'] });
      setDisconnectDialogOpen(false);
    },
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const showIncompleteWarning = status?.isConnected && status?.requiresAction;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Payment Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Connect your Stripe account to accept payments from clients on your invoices.
      </Typography>

      {/* Success/Error Alerts */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Stripe account connected successfully! You can now accept payments on invoices.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {decodeURIComponent(error)}
        </Alert>
      )}

      {/* Incomplete Setup Warning */}
      {showIncompleteWarning && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          Your Stripe account setup is incomplete. Click "Complete Setup" to finish connecting your account.
        </Alert>
      )}

      {/* Connection Status Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: status?.isConnected ? 'success.light' : 'grey.200',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CreditCardIcon
                sx={{ color: status?.isConnected ? 'success.dark' : 'grey.500' }}
              />
            </Box>
            <Box>
              <Typography variant="h6">Stripe Account</Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  size="small"
                  label={status?.isConnected ? 'Connected' : 'Not Connected'}
                  color={status?.isConnected ? 'success' : 'default'}
                />
                {status?.isConnected && status.chargesEnabled && (
                  <Chip size="small" label="Payments Enabled" color="info" variant="outlined" />
                )}
                {status?.accountType && (
                  <Chip
                    size="small"
                    label={status.accountType === 'standard' ? 'Standard' : 'Express'}
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>
          </Box>

          <Box display="flex" gap={1}>
            {status?.isConnected ? (
              <>
                {status.requiresAction && (
                  <Button
                    variant="contained"
                    onClick={handleCompleteSetup}
                    disabled={isConnecting}
                  >
                    {isConnecting ? 'Loading...' : 'Complete Setup'}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => setDisconnectDialogOpen(true)}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                startIcon={<LinkIcon />}
                onClick={() => setChoiceDialogOpen(true)}
              >
                Connect Stripe
              </Button>
            )}
          </Box>
        </Box>

        {status?.isConnected && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Account ID: {status.accountId}
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CheckCircleIcon
                  fontSize="small"
                  color={status.chargesEnabled ? 'success' : 'disabled'}
                />
                <Typography variant="body2">
                  Charges {status.chargesEnabled ? 'enabled' : 'disabled'}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={0.5}>
                <CheckCircleIcon
                  fontSize="small"
                  color={status.payoutsEnabled ? 'success' : 'disabled'}
                />
                <Typography variant="body2">
                  Payouts {status.payoutsEnabled ? 'enabled' : 'disabled'}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Paper>

      {/* Benefits Section */}
      {!status?.isConnected && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Why Connect Stripe?
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CreditCardIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Accept Card Payments"
                secondary="Let clients pay invoices instantly with credit/debit cards"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SpeedIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Get Paid Faster"
                secondary="Funds deposited directly to your bank account"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <SecurityIcon color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Secure & Trusted"
                secondary="Stripe handles all payment security and PCI compliance"
              />
            </ListItem>
          </List>
        </Paper>
      )}

      {/* Connection Choice Dialog */}
      <StripeConnectChoiceDialog
        open={choiceDialogOpen}
        onClose={() => setChoiceDialogOpen(false)}
        onConfirm={handleConnectionChoice}
        isLoading={isConnecting}
      />

      {/* Disconnect Confirmation Dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Disconnect Stripe Account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Disconnecting will prevent you from accepting payments on new invoices. Existing
            payment links will stop working. You can reconnect at any time.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={() => disconnectMutation.mutate()}
            color="error"
            variant="contained"
            disabled={disconnectMutation.isPending}
          >
            {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PaymentSettingsPage;
